<template>
    <section class="diabetes-risk-check">
        <progress-bar
            v-if="started && phase === 'quiz'"
            :question-number="currentIndex + 1"
            :number-of-tabs="questions.length"
            :show-back-button="currentIndex > 0"
            @goBack="back"
        ></progress-bar>

        <div class="container">
            <div v-if="!started" class="diabetes-risk-check__start">
                <span class="section-title text-color-default">Diabetes-Risiko-Check</span>
                <h1 class="new-headline-xlg">Wie hoch ist Ihr Risiko für Typ‑2‑Diabetes?</h1>
                <p class="new-text-lg">
                    Beantworten Sie 9 kurze Fragen und erhalten Sie eine erste Einschätzung Ihres Diabetes-Risikos für die nächsten 10 Jahre.
                </p>

                <ul class="list-w-check">
                    <li>
                    <figure>
                        <span class="material-symbols-rounded full-circle icon-check-list text-green">
                            <span>check</span>
                        </span>
                    </figure>
                    <span class="new-text-xl">9 Fragen</span>
                </li>
                    <li>
                    <figure>
                        <span class="material-symbols-rounded full-circle icon-check-list text-green">
                            <span>check</span>
                        </span>
                    </figure>
                    <span class="new-text-xl">2 Minuten</span>
                </li>
                    <li>
                    <figure>
                        <span class="material-symbols-rounded full-circle icon-check-list text-green">
                            <span>check</span>
                        </span>
                    </figure>
                    <span class="new-text-xl">FINDRISK-basiert</span>
                </li>
                </ul>

                <p class="new-text-md diabetes-risk-check__note">
                    Dieser Test dient als erste Orientierung auf Basis des wissenschaftlich validierten FINDRISK-Fragebogens und ersetzt keine ärztliche Diagnose. Bei Fragen wenden Sie sich an Ihren Hausarzt.
                </p>
                <v-button href="#" label="Test starten" size="lg" position="center" @click.prevent.native="start"></v-button>
            </div>

            <form v-else-if="phase === 'quiz'" id="diabetes-risk-check-question" class="diabetes-risk-check__panel wrapper" role="form" method="post" @submit.prevent>
                <header class="diabetes-risk-check__question">
                    <span class="section-title text-color-default">{{ currentQuestion.kicker }}</span>
                    <h1 class="new-headline-md">{{ currentQuestion.question }}</h1>
                    <p v-if="currentQuestion.helper" class="new-text-md" v-html="currentQuestion.helper"></p>
                </header>

                <div v-if="currentQuestion.id === 'age'" class="diabetes-risk-check__range-group">
                    <div class="input-wrapper wrapper-range">
                        <div class="range-text">
                            <strong>{{ ageSlider.title }}</strong>
                            <div class="range-output">
                                <input
                                    :class="'input-range-risk-' + ageSlider.id"
                                    type="number"
                                    placeholder="0"
                                    :value="ageSlider.value > 0 ? ageSlider.value : null"
                                >
                                <strong class="range-unit headline-xs">{{ ageSlider.unit }}</strong>
                            </div>
                        </div>
                        <input
                            :id="'risk-check-' + ageSlider.id"
                            :class="'input-range risk-range-' + ageSlider.id"
                            type="range"
                            :min="ageSlider.min"
                            :max="ageSlider.max"
                            step="1"
                            :value="ageSlider.value"
                            data-rangeslider
                        >

                        <form-input-error v-if="ageSlider.error" :error="ageSlider.error"></form-input-error>
                    </div>

                    <v-button
                        label="Weiter"
                        size="lg"
                        full-width
                        :disabled="!ageValue"
                        @onClick="continueFromAge"
                    ></v-button>
                </div>

                <div v-if="currentQuestion.id === 'bmi'" class="diabetes-risk-check__range-group">
                    <div v-for="slider in bmiSliders" :key="slider.id" class="input-wrapper wrapper-range">
                        <div class="range-text">
                            <strong>{{ slider.title }}</strong>
                            <div class="range-output">
                                <input
                                    :class="'input-range-risk-' + slider.id"
                                    type="number"
                                    placeholder="0"
                                    :value="slider.value > 0 ? slider.value : null"
                                >
                                <strong class="range-unit headline-xs">{{ slider.unit }}</strong>
                            </div>
                        </div>
                        <input
                            :id="'risk-check-' + slider.id"
                            :class="'input-range risk-range-' + slider.id"
                            type="range"
                            :min="slider.min"
                            :max="slider.max"
                            step="1"
                            :value="slider.value"
                            data-rangeslider
                        >

                        <form-input-error v-if="slider.error" :error="slider.error"></form-input-error>
                    </div>

                    <p v-if="bmiValue" class="new-text-lg text-centered diabetes-risk-check__bmi-result">
                        Ihr berechneter BMI: <strong>{{ bmiValue }}</strong>
                    </p>

                    <v-button
                        label="Weiter"
                        size="lg"
                        full-width
                        :disabled="!bmiValue"
                        @onClick="continueFromBmi"
                    ></v-button>
                </div>

                <div v-if="currentQuestion.id === 'gender'" class="diabetes-risk-check__answers diabetes-risk-check__gender-options form-input-radio-button-wrapper-two-columns">
                    <div
                        v-for="option in answerOptions"
                        :key="option.id"
                        class="form-input-radio-button-big form-input-radio-button--purple"
                        :class="{ active: isSelected(option), 'diabetes-risk-check__option--loading': isSelected(option) && transitioning }"
                        role="button"
                        tabindex="0"
                        @click="selectOption(option, true)"
                        @keyup.enter="selectOption(option, true)"
                        @keyup.space="selectOption(option, true)"
                    >
                        <div class="form-input-radio-button-inner">
                            <figure class="form-input-radio-button-icon">
                                <img :src="optionIcon(option)" :alt="option.label">
                            </figure>
                            <span class="label-static">{{ option.label }}</span>
                        </div>
                    </div>
                </div>

                <div v-if="isBinaryYesNoQuestion" class="diabetes-risk-check__answers diabetes-risk-check__yes-no form-input-radio-button-wrapper-two-columns">
                    <div
                        v-for="option in binaryAnswerOptions"
                        :key="option.id"
                        :class="[
                            'form-input-radio-button-big',
                            binaryOptionClass(option),
                            { active: isSelected(option), 'diabetes-risk-check__option--loading': isSelected(option) && transitioning }
                        ]"
                        role="button"
                        tabindex="0"
                        @click="selectOption(option, true)"
                        @keyup.enter="selectOption(option, true)"
                        @keyup.space="selectOption(option, true)"
                    >
                        <div class="form-input-radio-button-inner">
                            <figure class="form-input-radio-button-icon">
                                <span class="material-symbols-rounded">{{ binaryOptionIcon(option) }}</span>
                            </figure>
                            <span class="label-static">{{ option.label }}</span>
                        </div>
                    </div>
                </div>

                <div v-if="currentQuestion.id !== 'age' && currentQuestion.id !== 'bmi' && currentQuestion.id !== 'gender' && !isBinaryYesNoQuestion" class="diabetes-risk-check__answers form-input-radio-button-wrapper">
                    <div
                        v-for="option in answerOptions"
                        :key="option.id"
                        class="form-input-radio-button"
                        :class="{ active: isSelected(option), 'diabetes-risk-check__option--loading': isSelected(option) && transitioning }"
                        role="button"
                        tabindex="0"
                        @click="selectOption(option, true)"
                        @keyup.enter="selectOption(option, true)"
                        @keyup.space="selectOption(option, true)"
                    >
                        <div class="form-input-radio-button-inner">
                            <span class="label-static">{{ option.label }}</span>
                            <figure class="form-input-radio-button-selector">
                                <template v-if="isSelected(option)">
                                    <svg v-if="transitioning" aria-hidden="true" focusable="false" data-prefix="fas" data-icon="spinner" class="svg-inline--fa fa-spinner fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M304 48c0 26.51-21.49 48-48 48s-48-21.49-48-48 21.49-48 48-48 48 21.49 48 48zm-48 368c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48-21.49-48-48-48zm208-208c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48-21.49-48-48-48zM96 256c0-26.51-21.49-48-48-48S0 229.49 0 256s21.49 48 48 48 48-21.49 48-48zm12.922 99.078c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48c0-26.509-21.491-48-48-48zm294.156 0c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48c0-26.509-21.49-48-48-48zM108.922 60.922c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48-21.491-48-48-48z"></path></svg>
                                    <span v-else class="material-symbols-rounded check">check</span>
                                </template>
                                <span v-else></span>
                            </figure>
                        </div>
                        <p v-if="option.detail">{{ option.detail }}</p>
                    </div>
                </div>

            </form>

            <div v-else class="diabetes-risk-check__gate get-result">
                <figure>
                    <svg width="70" height="70" viewBox="0 0 70 70" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="70" height="70" rx="35" fill="#54DBA3"/>
                        <path d="M28.1823 44.408L52.2448 20.3455C52.6904 19.8999 53.2251 19.6771 53.849 19.6771C54.4728 19.6771 55.0298 19.8999 55.52 20.3455C55.9656 20.7911 56.1884 21.3481 56.1884 22.0165C56.1884 22.6403 55.9656 23.1751 55.52 23.6207L29.7865 49.3542C29.3409 49.7998 28.8061 50.0226 28.1823 50.0226C27.5584 50.0226 27.0237 49.7998 26.5781 49.3542L14.4132 37.1892C13.9676 36.7436 13.7448 36.2089 13.7448 35.5851C13.7894 34.9167 14.0344 34.3597 14.48 33.9141C14.9256 33.4685 15.4604 33.2457 16.0842 33.2457C16.7526 33.2457 17.3096 33.4685 17.7552 33.9141L28.1823 44.408Z" fill="white"/>
                    </svg>
                </figure>

                <h1 class="new-headline-md text-centered">Ihr Ergebnis wartet auf Sie</h1>

                <p class="new-text-lg text-color-default text-centered">Danke, dass Sie sich die Zeit genommen haben. Bitte geben Sie Ihre E-Mail-Adresse ein, um Ihr Ergebnis zu erhalten.</p>

                <form id="diabetes-risk-check-form" role="form" method="POST" action="#" @submit.prevent="submit" novalidate>
                    <form-input-type-email
                        name="email"
                        label="E-Mail-Adresse"
                        :errors="errors.email"
                        full-border
                        required
                    ></form-input-type-email>

                    <form-input-type-checkbox
                        name="agreements"
                        label='Durch Eingabe Ihres Namens und Ihrer E-Mail-Adresse erklären Sie sich mit den Bestimmungen zum <a href="/datenschutzerklarung" target="_blank">Datenschutz</a> einverstanden.'
                        :errors="errors.agreements"
                        required
                    ></form-input-type-checkbox>

                    <div class="diabetes-risk-check__gate-actions">
                        <v-button label="Ergebnis anzeigen" type="submit" position="center" size="lg"></v-button>
                        <v-button v-if="isSoftGate" label="Überspringen" variant="ghost" size="lg" position="center" @click="goToResult"></v-button>
                    </div>
                </form>

            </div>
        </div>
    </section>
</template>

<script>
export default {
    data() {
        return {
            started: false,
            phase: 'quiz',
            currentIndex: 0,
            transitioning: false,
            selectedAnswerIds: {},
            answers: {},
            gender: null,
            ageSlider: {
                id: 'age',
                min: 18,
                max: 90,
                unit: 'Jahre',
                title: 'Alter',
                value: 0,
                error: null
            },
            bmiSliders: [
                {
                    id: 'weight',
                    min: 40,
                    max: 250,
                    unit: 'kg',
                    title: 'Gewicht',
                    value: 0,
                    error: null
                },
                {
                    id: 'height',
                    min: 140,
                    max: 220,
                    unit: 'cm',
                    title: 'Größe',
                    value: 0,
                    error: null
                }
            ],
            totalScore: 0,
            errors: {},
            questions: [
                {
                    id: 'gender',
                    kicker: 'Profil',
                    question: 'Bitte wählen Sie Ihr Geschlecht.',
                    helper: 'Diese Angabe wird nur für die passenden Grenzwerte beim Bauchumfang verwendet.',
                    options: [
                        {
                            id: 'female',
                            label: 'Frau',
                            points: 0,
                            resources: {
                                normal: '/imgs/forms/icon_female@3x.png',
                                active: '/imgs/forms/icon_female_active@3x.png',
                                inactive: '/imgs/forms/icon_female_inactive@3x.png'
                            }
                        },
                        {
                            id: 'male',
                            label: 'Mann',
                            points: 0,
                            resources: {
                                normal: '/imgs/forms/icon_male@3x.png',
                                active: '/imgs/forms/icon_male_active@3x.png',
                                inactive: '/imgs/forms/icon_male_inactive@3x.png'
                            }
                        }
                    ]
                },
                {
                    id: 'age',
                    kicker: 'Alter',
                    question: 'Wie alt sind Sie?',
                    options: [
                        { id: 'under_35', label: 'Unter 35 Jahre', points: 0 },
                        { id: '35_44', label: '35-44 Jahre', points: 1 },
                        { id: '45_54', label: '45-54 Jahre', points: 2 },
                        { id: '55_64', label: '55-64 Jahre', points: 3 },
                        { id: 'over_64', label: 'Über 64 Jahre', points: 4 }
                    ]
                },
                {
                    id: 'bmi',
                    kicker: 'Body-Mass-Index',
                    question: 'Wie groß und wie schwer sind Sie?',
                    helper: 'Wir berechnen daraus automatisch Ihren Body-Mass-Index.',
                    options: [
                        { id: 'under_25', label: 'Unter 25', points: 0 },
                        { id: '25_30', label: '25-30', points: 1 },
                        { id: 'over_30', label: 'Über 30', points: 3 }
                    ]
                },
                {
                    id: 'waist',
                    kicker: 'Bauchumfang',
                    question: 'Welchen Bauchumfang haben Sie?',
                    helper: 'Messen Sie auf Höhe des Bauchnabels.',
                    options: []
                },
                {
                    id: 'activity',
                    kicker: 'Bewegung',
                    question: 'Bewegen Sie sich täglich mindestens 30 Minuten?',
                    helper: 'Zum Beispiel Spaziergang, Radfahren, Gartenarbeit oder Sport.',
                    options: [
                        { id: 'yes', label: 'Ja', points: 0 },
                        { id: 'no', label: 'Nein', points: 2 }
                    ]
                },
                {
                    id: 'diet',
                    kicker: 'Ernährung',
                    question: 'Wie oft essen Sie Obst, Gemüse oder Vollkornprodukte?',
                    options: [
                        { id: 'daily', label: 'Täglich', points: 0 },
                        { id: 'not_daily', label: 'Nicht täglich', points: 1 }
                    ]
                },
                {
                    id: 'blood_pressure',
                    kicker: 'Blutdruckmedikamente',
                    question: 'Nehmen Sie Medikamente gegen Bluthochdruck ein?',
                    options: [
                        { id: 'no', label: 'Nein', points: 0 },
                        { id: 'yes', label: 'Ja', points: 2 }
                    ]
                },
                {
                    id: 'blood_sugar',
                    kicker: 'Blutzucker-Vorgeschichte',
                    question: 'Wurde bei Ihnen jemals ein erhöhter Blutzucker festgestellt?',
                    helper: 'Zum Beispiel bei einer Vorsorgeuntersuchung, während einer Schwangerschaft oder bei einem Krankenhausaufenthalt.',
                    options: [
                        { id: 'no', label: 'Nein', points: 0 },
                        { id: 'yes', label: 'Ja', points: 5 }
                    ]
                },
                {
                    id: 'family_history',
                    kicker: 'Familienanamnese',
                    question: 'Hat jemand in Ihrer Familie Diabetes?',
                    helper: 'Gemeint ist Typ 1 oder Typ 2 Diabetes.',
                    options: [
                        { id: 'no', label: 'Nein', points: 0 },
                        { id: 'extended', label: 'Ja: Großeltern, Tante, Onkel, Cousin/Cousine', points: 3 },
                        { id: 'close', label: 'Ja: Eltern, Geschwister oder eigenes Kind', points: 5 }
                    ]
                }
            ]
        }
    },

    computed: {
        currentQuestion() {
            return this.questions[this.currentIndex]
        },

        answerOptions() {
            if (this.currentQuestion.id === 'age' || this.currentQuestion.id === 'bmi') {
                return []
            }

            if (this.currentQuestion.id === 'waist') {
                return this.waistOptions()
            }

            return this.currentQuestion.options
        },

        isBinaryYesNoQuestion() {
            return ['activity', 'blood_pressure', 'blood_sugar'].indexOf(this.currentQuestion.id) !== -1
        },

        binaryAnswerOptions() {
            if (!this.isBinaryYesNoQuestion) {
                return []
            }

            const yes = this.currentQuestion.options.find((option) => option.id === 'yes')
            const no = this.currentQuestion.options.find((option) => option.id === 'no')

            return [yes, no].filter((option) => option)
        },

        ageValue() {
            return parseInt(this.ageSlider.value, 10) || null
        },

        bmiValue() {
            const weight = parseFloat(this.bmiSlider('weight').value)
            const height = parseFloat(this.bmiSlider('height').value)

            if (!weight || !height) {
                return null
            }

            const meters = height / 100

            if (meters <= 0) {
                return null
            }

            return Math.round((weight / (meters * meters)) * 10) / 10
        },

        riskLevel() {
            if (this.totalScore <= 6) {
                return 'low'
            }

            if (this.totalScore <= 11) {
                return 'slightly_elevated'
            }

            if (this.totalScore <= 14) {
                return 'moderate'
            }

            if (this.totalScore <= 20) {
                return 'high'
            }

            return 'very_high'
        },

        isSoftGate() {
            return this.totalScore >= 7 && this.totalScore <= 11
        }
    },

    watch: {
        currentIndex(index) {
            if (this.questions[index].id === 'age' || this.questions[index].id === 'bmi') {
                this.$nextTick(this.initCurrentSliders)
            }
        },

        ageValue(value) {
            if (this.currentQuestion.id !== 'age' || value === null) {
                return
            }

            this.storeAgeAnswer()
        },

        bmiValue(value) {
            if (this.currentQuestion.id !== 'bmi' || value === null) {
                return
            }

            this.storeBmiAnswer()
        }
    },

    methods: {
        start() {
            this.started = true
            window.scrollTo(0, 0)
            this.$nextTick(this.initCurrentSliders)
        },

        currentSliders() {
            if (this.currentQuestion.id === 'age') {
                return [this.ageSlider]
            }

            if (this.currentQuestion.id === 'bmi') {
                return this.bmiSliders
            }

            return []
        },

        initCurrentSliders() {
            this.initRangeSliders(this.currentSliders())
        },

        bmiSlider(id) {
            return this.bmiSliders.find((slider) => slider.id === id)
        },

        questionById(id) {
            return this.questions.find((question) => question.id === id)
        },

        initRangeSliders(sliders) {
            sliders.forEach((slider) => {
                const range = $('.risk-range-' + slider.id)
                const input = $('.input-range-risk-' + slider.id)

                if (!range.length) {
                    return
                }

                input.off('change.diabetesRiskCheck').on('change.diabetesRiskCheck', () => {
                    const value = parseInt(input.val(), 10)

                    if (value >= slider.min && value <= slider.max) {
                        slider.value = value
                        range.val(value).trigger('input').change()
                    } else {
                        slider.error = 'Der eingegebene Wert muss zwischen ' + slider.min + ' - ' + slider.max + ' ' + slider.unit + ' liegen.'
                    }
                })

                range.off('input.diabetesRiskCheck change.diabetesRiskCheck').on('input.diabetesRiskCheck change.diabetesRiskCheck', () => {
                    slider.value = parseInt(range.val(), 10)
                    slider.error = null
                })

                if (typeof range.rangeslider !== 'function' || range.next('.rangeslider').length) {
                    return
                }

                range.rangeslider({
                    polyfill: false,
                    onSlide: (position, value) => {
                        slider.value = value
                        slider.error = null
                    }
                })
            })
        },

        ageOption() {
            const ageQuestion = this.questionById('age')

            if (this.ageValue === null) {
                return null
            }

            if (this.ageValue < 35) {
                return ageQuestion.options.find((item) => item.id === 'under_35')
            }

            if (this.ageValue <= 44) {
                return ageQuestion.options.find((item) => item.id === '35_44')
            }

            if (this.ageValue <= 54) {
                return ageQuestion.options.find((item) => item.id === '45_54')
            }

            if (this.ageValue <= 64) {
                return ageQuestion.options.find((item) => item.id === '55_64')
            }

            return ageQuestion.options.find((item) => item.id === 'over_64')
        },

        storeAgeAnswer() {
            const ageQuestion = this.questionById('age')
            const option = this.ageOption()

            if (!option) {
                return
            }

            this.$set(this.selectedAnswerIds, 'age', option.id)
            this.$set(this.answers, 'age', {
                id: 'age',
                question: ageQuestion.question,
                answer_id: option.id,
                answer: option.label,
                points: option.points,
                age: this.ageValue,
                gender: null,
                bmi: null
            })
            this.countTotalScore()
        },

        continueFromAge() {
            if (!this.ageValue || this.transitioning) {
                return
            }

            this.storeAgeAnswer()
            this.transitioning = true

            setTimeout(() => {
                this.currentIndex = this.currentIndex + 1
                window.scrollTo(0, 0)
                this.transitioning = false
            }, 250)
        },

        bmiOption() {
            const bmiQuestion = this.questionById('bmi')

            if (this.bmiValue === null) {
                return null
            }

            if (this.bmiValue < 25) {
                return bmiQuestion.options.find((item) => item.id === 'under_25')
            }

            if (this.bmiValue <= 30) {
                return bmiQuestion.options.find((item) => item.id === '25_30')
            }

            return bmiQuestion.options.find((item) => item.id === 'over_30')
        },

        storeBmiAnswer() {
            const bmiQuestion = this.questionById('bmi')
            const option = this.bmiOption()

            if (!option) {
                return
            }

            this.$set(this.selectedAnswerIds, 'bmi', option.id)
            this.$set(this.answers, 'bmi', {
                id: 'bmi',
                question: bmiQuestion.question,
                answer_id: option.id,
                answer: option.label,
                points: option.points,
                gender: null,
                weight: this.bmiSlider('weight').value,
                height: this.bmiSlider('height').value,
                bmi: this.bmiValue
            })
            this.countTotalScore()
        },

        continueFromBmi() {
            if (!this.bmiValue || this.transitioning) {
                return
            }

            this.storeBmiAnswer()
            this.transitioning = true

            setTimeout(() => {
                this.currentIndex = this.currentIndex + 1
                window.scrollTo(0, 0)
                this.transitioning = false
            }, 250)
        },

        waistOptions() {
            if (this.gender === 'male') {
                return [
                    { id: 'normal', label: 'Normal', detail: 'Unter 94 cm', points: 0 },
                    { id: 'increased', label: 'Erhöht', detail: '94-102 cm', points: 3 },
                    { id: 'high', label: 'Stark erhöht', detail: 'Über 102 cm', points: 4 }
                ]
            }

            return [
                { id: 'normal', label: 'Normal', detail: 'Unter 80 cm', points: 0 },
                { id: 'increased', label: 'Erhöht', detail: '80-88 cm', points: 3 },
                { id: 'high', label: 'Stark erhöht', detail: 'Über 88 cm', points: 4 }
            ]
        },

        isSelected(option) {
            return this.selectedAnswerIds[this.currentQuestion.id] === option.id
        },

        optionIcon(option) {
            if (!option.resources) {
                return null
            }

            return this.isSelected(option) ? option.resources.active : option.resources.normal
        },

        binaryOptionClass(option) {
            return option.id === 'no' ? 'form-input-radio-button--danger' : ''
        },

        binaryOptionIcon(option) {
            return option.id === 'no' ? 'close' : 'done'
        },

        selectOption(option, shouldAdvance) {
            if (this.transitioning) {
                return
            }

            if (this.currentQuestion.id === 'gender') {
                const genderChanged = this.gender !== null && this.gender !== option.id
                this.gender = option.id

                if (genderChanged) {
                    this.$delete(this.selectedAnswerIds, 'waist')
                    this.$delete(this.answers, 'waist')
                }
            }

            this.$set(this.selectedAnswerIds, this.currentQuestion.id, option.id)
            this.$set(this.answers, this.currentQuestion.id, {
                id: this.currentQuestion.id,
                question: this.currentQuestion.question,
                answer_id: option.id,
                answer: option.detail ? option.label + ' (' + option.detail + ')' : option.label,
                points: option.points,
                gender: this.currentQuestion.id === 'gender' || this.currentQuestion.id === 'waist' ? this.gender : null,
                bmi: this.currentQuestion.id === 'bmi' ? this.bmiValue : null
            })
            this.countTotalScore()

            if (!shouldAdvance) {
                return
            }

            this.transitioning = true

            setTimeout(() => {
                if (this.currentIndex < this.questions.length - 1) {
                    this.currentIndex = this.currentIndex + 1
                    window.scrollTo(0, 0)
                } else {
                    this.finish()
                }

                this.transitioning = false
            }, 450)
        },

        countTotalScore() {
            this.totalScore = Object.keys(this.answers).reduce((score, key) => {
                return score + this.answers[key].points
            }, 0)
        },

        finish() {
            this.countTotalScore()

            if (this.totalScore <= 6) {
                this.goToResult()
                return
            }

            this.phase = 'gate'
            window.scrollTo(0, 0)
        },

        back() {
            if (this.currentIndex === 0) {
                return
            }

            this.currentIndex = this.currentIndex - 1
            window.scrollTo(0, 0)
        },

        orderedAnswers() {
            return this.questions.map((question) => this.answers[question.id]).filter((answer) => answer)
        },

        payload() {
            return {
                quiz_type: 'diabetes_risk_check',
                score: this.totalScore,
                risk_level: this.riskLevel,
                answers: this.orderedAnswers()
            }
        },

        submit() {
            let formData = {}

            $('#diabetes-risk-check-form')
                .serializeArray()
                .forEach(({ name, value }) => {
                    formData[name] = value
                })

            formData.quiz_type = 'diabetes_risk_check'
            formData.score = this.totalScore
            formData.answers = JSON.stringify(this.payload())

            axios
                .post('/api/patient-quiz-form', formData, {
                    headers: {
                        'Accept-Language': 'de'
                    }
                })
                .then(() => {
                    this.goToResult()
                })
                .catch((e) => {
                    this.errors = e.response && e.response.data ? e.response.data.errors : {}
                    Vuemit.fire('Button--sending-failed', true)
                })
        },

        goToResult() {
            window.location.replace('/diabetes-risiko-check/result?score=' + this.totalScore)
        }
    }
}
</script>
